import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'

import { useFormatPrice } from '@src/blocks/currency'
import { getOfferPricesForProductCard, getPriceStyleKey } from '@src/quince-site-common/src/utils/festivalOffersUtility'

import type { ProductTilePriceComputed, ProductTilePriceProps } from './price.types'
import type { SystemStyleObject } from '@lastbrand/web-ui-styled-system/types'

const PRICE_TEXT_STYLE = {
  mini: { base: 'body-12', lg: 'body-14' },
  compact: { base: 'body-12', lg: 'body-14' },
  // body-12 on desktop too, matching the defaultSM tile body in 6-up grids (Bestsellers / BackToSchool).
  defaultSM: { base: 'body-12', lg: 'body-12' },
  default: { base: 'body-12', lg: 'body-16' },
} as const

const PRICE_SALE_COLOR = {
  // eslint-disable-next-line i18next/no-literal-string -- DS color token, not translatable copy
  promo: 'promo' as const,
  // eslint-disable-next-line i18next/no-literal-string -- DS color token, not translatable copy
  neutral: 'neutral' as const,
} as const

function hasRenderablePriceCompare(node: string | undefined): boolean {
  if (node == null) return false
  return node.trim().length > 0
}

export function useProductTilePrice({
  selectedVariant,
  subscriptionEnabled = false,
  productPromoConfig,
}: ProductTilePriceProps): ProductTilePriceComputed {
  const formatPrice = useFormatPrice()

  return useMemo(() => {
    const salePrice = selectedVariant?.price?.salePrice
    const showFrom = selectedVariant?.price?.isRange
    const formattedStandardPrice = formatPrice(salePrice ?? 0)

    const { festivalPrice, subscriptionPrice } = getOfferPricesForProductCard(
      subscriptionEnabled,
      selectedVariant,
      productPromoConfig,
    )
    const offerPrice = subscriptionPrice ?? festivalPrice
    const formattedOfferPrice = offerPrice ? formatPrice(offerPrice) : undefined
    const hasOffer = Boolean(formattedOfferPrice)

    const priceSale = formattedOfferPrice ?? formattedStandardPrice
    const priceCompareAt = hasOffer ? formattedStandardPrice : undefined
    // Match legacy product-card colour semantics (product-title-section.module.scss):
    // festival offers are promo (red), subscription offers stay neutral (black).
    // getPriceStyleKey gives festival precedence when both are present.
    const priceStyleKey = getPriceStyleKey(Boolean(festivalPrice), Boolean(subscriptionPrice))
    const priceSaleColor = priceStyleKey === 'festival' ? PRICE_SALE_COLOR.promo : PRICE_SALE_COLOR.neutral
    const showPriceFrom = Boolean(showFrom && !offerPrice)

    return {
      priceSale,
      priceCompareAt,
      priceSaleColor,
      showPriceFrom,
      hasOffer,
      formattedStandardPrice,
      formattedOfferPrice,
    }
  }, [formatPrice, productPromoConfig, selectedVariant, subscriptionEnabled])
}

/**
 * Product tile price row aligned with {@link ProductTile} typography (default / compact / mini).
 * Prefer {@link useProductTilePrice} when you only need computed strings.
 */
export function ProductTilePrice({
  tileVariant = 'default',
  className,
  compareAtFirst = false,
  ...priceInputs
}: ProductTilePriceProps) {
  const { t } = useTranslation('common')
  const computed = useProductTilePrice(priceInputs)
  const priceFromLabel = t('from')
  // Legacy custom-footer parity: `from` shows for range pricing even when an offer applies.
  const showFromLabel = compareAtFirst ? Boolean(priceInputs.selectedVariant?.price?.isRange) : computed.showPriceFrom

  const isMini = tileVariant === 'mini'
  const isCompact = tileVariant === 'compact'
  const isDefaultSM = tileVariant === 'defaultSM'
  // Layout follows the presence of an offer (sale + strikethrough compare), not the colour —
  // subscription offers keep the offer layout while rendering neutral (black).
  const hasOfferLayout = computed.hasOffer
  /** Matches @lastbrand/web-ui/panda-config/text-styles.ts `body-12` | `body-14` | `body-16` (responsive). */
  const priceTextStyle = (
    isMini
      ? PRICE_TEXT_STYLE.mini
      : isCompact
        ? PRICE_TEXT_STYLE.compact
        : isDefaultSM
          ? PRICE_TEXT_STYLE.defaultSM
          : PRICE_TEXT_STYLE.default
  ) as SystemStyleObject['textStyle']

  const priceSaleNode = computed.priceSale
  const priceCompareAtNode = computed.priceCompareAt

  if (isMini) {
    const stackBelowLg = compareAtFirst && computed.hasOffer
    const compareAtSpan = hasRenderablePriceCompare(priceCompareAtNode) ? (
      <span
        className={css({
          textStyle: priceTextStyle,
          color: 'mediumgray',
          textDecoration: 'line-through',
          textDecorationSkipInk: 'none',
          flexShrink: 0,
        })}
      >
        {priceCompareAtNode}
      </span>
    ) : null
    const fromSpan = showFromLabel ? (
      <span
        className={css({
          textStyle: priceTextStyle,
          color: compareAtFirst || computed.priceSaleColor === PRICE_SALE_COLOR.neutral ? 'black' : 'red-100',
          flexShrink: 0,
        })}
      >
        {priceFromLabel}
      </span>
    ) : null
    return (
      <div
        className={css(
          {
            display: 'flex',
            flexDirection: stackBelowLg ? { base: 'column', lg: 'row' } : 'row',
            flexWrap: 'wrap',
            alignItems: stackBelowLg ? { base: 'flex-start', lg: 'flex-end' } : 'flex-end',
            gap: stackBelowLg ? { base: '0', lg: 'space-4' } : 'space-4',
            width: 'full',
            minWidth: 0,
          },
          className,
        )}
      >
        {compareAtFirst && compareAtSpan ? (
          // `from` + compare-at share one row so the stacked layout matches legacy ("From ~~$X~~" / "$Y").
          <span
            className={css({
              display: 'flex',
              flexDirection: 'row',
              gap: 'space-4',
              alignItems: 'flex-end',
              flexShrink: 0,
            })}
          >
            {fromSpan}
            {compareAtSpan}
          </span>
        ) : (
          fromSpan
        )}
        <span
          className={css({
            textStyle: priceTextStyle,
            color: computed.priceSaleColor === PRICE_SALE_COLOR.neutral ? 'black' : 'red-100',
            flexShrink: 0,
          })}
        >
          {priceSaleNode}
        </span>
        {compareAtFirst ? null : compareAtSpan}
      </div>
    )
  }

  const compareAtBlock = hasRenderablePriceCompare(priceCompareAtNode) ? (
    <div
      className={css({
        textStyle: priceTextStyle,
        color: 'mediumgray',
        textDecoration: 'line-through',
        textDecorationSkipInk: 'none',
      })}
    >
      {priceCompareAtNode}
    </div>
  ) : null

  return (
    <div
      className={css(
        {
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: { base: hasOfferLayout ? 'flex-end' : 'flex-start', lg: 'flex-end' },
          textAlign: { base: hasOfferLayout ? 'right' : 'left', lg: 'right' },
          whiteSpace: 'nowrap',
        },
        className,
      )}
    >
      {compareAtFirst ? compareAtBlock : null}
      <div
        className={css({
          display: 'flex',
          flexDirection: { base: hasOfferLayout ? 'row' : 'column', lg: 'row' },
          gap: { base: hasOfferLayout ? 'space-2' : 0, lg: 'space-2' },
          alignItems: 'flex-start',
          justifyContent: { base: hasOfferLayout ? 'flex-end' : undefined, lg: undefined },
          textStyle: priceTextStyle,
          color: computed.priceSaleColor === PRICE_SALE_COLOR.neutral ? 'black' : 'red-100',
        })}
      >
        {showFromLabel ? <span>{priceFromLabel}</span> : null}
        <span>{priceSaleNode}</span>
      </div>
      {compareAtFirst ? null : compareAtBlock}
    </div>
  )
}
